import React, { useCallback, useMemo, useRef, useState, forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { SLIDE_INDEX_360 } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { getVdpPfButtonSROnlyText } from 'site-modules/shared/utils/inventory/get-vdp-pf-button-text';
import { fireCarouselTracking } from 'site-modules/shared/utils/photoflipper/photoflipper';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { InventoryHeroCarouselImage } from 'site-modules/inventory/components/inventory-hero/inventory-hero-carousel-image/inventory-hero-carousel-image';
import { Photo360Indicator } from 'site-modules/shared/components/photo-360-indicator/photo-360-indicator';
import { CarouselLazySlide } from 'site-modules/shared/components/usurp-inventory-card/usurp-inventory-card-carousel/carousel-lazy-slide';
import { CarouselMetaContext } from 'site-modules/shared/components/usurp-inventory-card/usurp-inventory-card-carousel/carousel-meta-context';

import './inventory-hero-carousel.scss';

const DEFAULT_ANIMATION_SPEED = 500;

export const InventoryHeroCarousel = memo(
  forwardRef(
    (
      {
        showSliderArrows = false,
        photoUrls = [],
        photoContainerAspectRatio,
        make,
        model,
        year,
        vin,
        isMobile = false,
        bodyType = '',
        hasPhoto360 = false,
        onOpenPhotoflipper,
        onIndexChange,
        photoCount = 0,
      },
      ref
    ) => {
      const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
      const isSwiping = useRef(false);

      const beforeSlideChange = useCallback(
        (from, to) => {
          isSwiping.current = true;
          setCurrentSlideIndex(to);
          onIndexChange(to);

          const slidePhotoType = hasPhoto360 && to === SLIDE_INDEX_360 ? '360' : get(photoUrls[to], 'photoType');

          fireCarouselTracking({
            target: get(ref, 'current.element', {}),
            currentSlide: from,
            nextSlide: to,
            total: photoUrls.length,
            contentType: 'content',
            contentDetails: 'content',
            photoType: slidePhotoType,
          });
        },
        [hasPhoto360, onIndexChange, photoUrls, ref]
      );

      const afterSlideChange = useCallback(() => {
        isSwiping.current = false;
      }, []);

      const metaContextValue = useMemo(
        () => ({
          currentSlide: currentSlideIndex,
        }),
        [currentSlideIndex]
      );

      const handleOpenPhotoflipper = useCallback(() => {
        if (!isSwiping.current) {
          onOpenPhotoflipper();
        }
      }, [onOpenPhotoflipper]);

      const carouselSlides = useMemo(
        () =>
          photoUrls.map(({ url }, index) => (
            <CarouselLazySlide key={url} slideIndex={index}>
              <AspectRatioContainer aspectRatio={photoContainerAspectRatio}>
                <div>
                  <InventoryHeroCarouselImage
                    make={make}
                    model={model}
                    year={year}
                    url={url}
                    bodyType={bodyType}
                    vin={vin}
                    isMobile={isMobile}
                    className={classNames('bg-white', {
                      'rounded-12': isMobile,
                    })}
                  />
                </div>

                <button
                  className="hero-carousel-btn bg-transparent w-100 h-100 border-0 p-0 pos-a"
                  onClick={handleOpenPhotoflipper}
                  data-tracking-id="open_legacy_photoflipper"
                  data-no-refresh
                  aria-haspopup="dialog"
                  tabIndex={currentSlideIndex !== index ? -1 : undefined}
                >
                  {hasPhoto360 && index === SLIDE_INDEX_360 && (
                    <div className="pos-a top-0">
                      <Photo360Indicator />
                    </div>
                  )}
                  <span className="visually-hidden">{getVdpPfButtonSROnlyText(photoCount)}</span>
                </button>
              </AspectRatioContainer>
            </CarouselLazySlide>
          )),
        [
          bodyType,
          currentSlideIndex,
          handleOpenPhotoflipper,
          hasPhoto360,
          isMobile,
          make,
          model,
          photoContainerAspectRatio,
          photoCount,
          photoUrls,
          vin,
          year,
        ]
      );

      return (
        <CarouselMetaContext.Provider value={metaContextValue}>
          <Carousel
            infinite={false}
            arrows={showSliderArrows}
            beforeChange={beforeSlideChange}
            afterChange={afterSlideChange}
            speed={DEFAULT_ANIMATION_SPEED}
            data-carousel-photo-type={get(photoUrls[currentSlideIndex], 'photoType')}
            ariaLive="off"
            ref={ref}
            trackSwipeEvent={false}
          >
            {carouselSlides}
          </Carousel>
        </CarouselMetaContext.Provider>
      );
    }
  )
);

InventoryHeroCarousel.propTypes = {
  showSliderArrows: PropTypes.bool,
  photoUrls: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string, photoType: PropTypes.string })),
  photoContainerAspectRatio: PropTypes.string,
  make: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  vin: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  bodyType: PropTypes.string,
  hasPhoto360: PropTypes.bool,
  onOpenPhotoflipper: PropTypes.func.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  photoCount: PropTypes.number,
};

InventoryHeroCarousel.displayName = 'InventoryHeroCarousel';
